// Impact — forecast against realized, by lever and by initiative; the
// numbers recompute from the same model the editor uses, so what is shown
// is exactly what a saved forecast says. Export CSV for the board deck.
import React, { useMemo } from 'react';
import { LEVERS, LEVER_LABEL, computeForecast, realizedToDate, rollup } from '../../data/blueprintForecast';
import { usd } from './fields';
import { STATUS_TONE } from './Initiatives';
import { Chip } from '../ModuleFrame';

export default function Impact({ initiatives, opts, summary, onPick }) {
  const r = useMemo(() => rollup(initiatives, opts), [initiatives, opts]);
  const rows = useMemo(() => initiatives.filter((i) => i.status !== 'rejected').map((i) => ({ it: i, f: computeForecast(i.forecast || {}, { ...opts, lever: i.lever }), realized: realizedToDate(i.realized) })).sort((a, b) => b.f.annualSavingsUsd - a.f.annualSavingsUsd), [initiatives, opts]);
  const max = Math.max(1, ...Object.values(r.byLever).map((l) => Math.max(l.forecastAnnualUsd, l.realizedUsd)));
  const csv = () => {
    const head = ['title', 'lever', 'status', 'baselineUsd', 'reductionPct', 'oneTimeUsd', 'platformAnnualUsd', 'annualSavingsUsd', 'paybackMonths', 'npv3yUsd', 'realizedUsd'];
    const lines = [head.join(','), ...rows.map(({ it, f, realized }) => [JSON.stringify(it.title), it.lever, it.status, f.baselineTotalUsd, f.reductionPct, f.oneTimeUsd, f.platformAnnualUsd, f.annualSavingsUsd, f.paybackMonths ?? '', f.npv3yUsd, realized].join(','))];
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' })); a.download = 'blueprint-impact.csv'; a.click();
  };
  const months = summary?.months || null;
  return (
    <div data-nova-note={`Impact: ${r.active} active initiatives forecast ${usd(r.forecastAnnualUsd)}/yr, realized ${usd(r.realizedUsd)} to date, ${usd(r.suggestedAnnualUsd)}/yr still awaiting a decision.`}>
      <div className="bp-kpis">
        <div className="bp-kpi"><span>Forecast annual savings</span><strong>{usd(r.forecastAnnualUsd)}</strong><small>{r.active} accepted initiative{r.active === 1 ? '' : 's'}</small></div>
        <div className="bp-kpi"><span>Realized to date</span><strong>{usd(r.realizedUsd)}</strong><small>recorded + measured</small></div>
        <div className="bp-kpi"><span>One-time investment</span><strong>{usd(r.oneTimeUsd)}</strong><small>across the plan</small></div>
        <div className="bp-kpi"><span>Awaiting a decision</span><strong>{usd(r.suggestedAnnualUsd)}/yr</strong><small>{r.byStatus.suggested || 0} suggested</small></div>
      </div>
      <h3 className="bp-h3">By lever</h3>
      <div className="bp-bars">
        {LEVERS.map((l) => { const row = r.byLever[l.id]; if (!row) return null; return (
          <div key={l.id} className="bp-bar-row">
            <span className="bp-bar-label">{l.label} <span className="mf-muted">({row.count})</span></span>
            <div className="bp-bar-track"><div className="bp-bar bp-bar-forecast" style={{ width: `${(row.forecastAnnualUsd / max) * 100}%` }} title={`forecast ${usd(row.forecastAnnualUsd)}/yr`} /><div className="bp-bar bp-bar-realized" style={{ width: `${(row.realizedUsd / max) * 100}%` }} title={`realized ${usd(row.realizedUsd)}`} /></div>
            <span className="bp-bar-val">{usd(row.forecastAnnualUsd)}/yr · {usd(row.realizedUsd)}</span>
          </div>
        ); })}
        {!Object.keys(r.byLever).length && <p className="cs-help">Accept an initiative and its forecast shows up here.</p>}
      </div>
      {months && Object.keys(months).length > 0 && (
        <>
          <h3 className="bp-h3">Month by month</h3>
          <table className="mf-table"><thead><tr><th>Month</th><th>Forecast</th><th>Realized</th></tr></thead>
            <tbody>{Object.entries(months).sort().map(([m, v]) => <tr key={m}><td>{m}</td><td>{usd(v.forecastUsd)}</td><td>{usd(v.realizedUsd)}</td></tr>)}</tbody></table>
        </>
      )}
      <h3 className="bp-h3">By initiative <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={csv}>Export CSV</button></h3>
      <table className="mf-table">
        <thead><tr><th>Initiative</th><th>Lever</th><th>Status</th><th>Baseline</th><th>Savings / yr</th><th>Payback</th><th>NPV</th><th>Realized</th></tr></thead>
        <tbody>
          {rows.map(({ it, f, realized }) => (
            <tr key={it.id} style={{ cursor: 'pointer' }} onClick={() => onPick?.(it.id)}>
              <td>{it.title}</td><td className="mf-muted">{LEVER_LABEL[it.lever]}</td><td><Chip tone={STATUS_TONE[it.status]}>{it.status}</Chip></td>
              <td>{usd(f.baselineTotalUsd)}</td><td><strong>{usd(f.annualSavingsUsd)}</strong></td><td>{f.paybackMonths == null ? '—' : `${f.paybackMonths} mo`}</td><td>{usd(f.npv3yUsd)}</td><td>{usd(realized)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={8} className="mf-muted">No initiatives yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
